package org.teddapp.project.db

import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/**
 * Rejoue les micro-tâches récurrentes. Deux moments :
 *  - au démarrage, [catchUp] fait avancer les échéances dépassées jusqu'à aujourd'hui ;
 *  - à la complétion, [onCompleted] repose la tâche à sa prochaine occurrence.
 *
 * Rien n'est dupliqué : une tâche récurrente est une seule ligne qui revient.
 */
object RecurrenceScheduler {

    /** Au lancement : les tâches récurrentes en retard reviennent aujourd'hui, sans dette. */
    suspend fun catchUp(db: TeddDatabase, today: String = TODAY_ISO) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.taskQueries.selectAll().executeAsList()
            .filter { it.is_deleted == 0L && !it.recurrence.isNullOrBlank() && it.recurrence != TaskRecurrence.None.key }
            .forEach { task ->
                val rec = TaskRecurrence.of(task.recurrence)
                val next = catchUpOccurrence(rec, task.due_date, today) ?: return@forEach
                val mustReopen = task.status == "done"
                if (next != task.due_date || mustReopen) {
                    db.taskQueries.updateDue(next, now, task.id)
                    if (mustReopen) db.taskQueries.markPending(now, task.id)
                }
            }
    }

    /**
     * Une tâche récurrente vient d'être cochée : elle se repose à la prochaine occurrence
     * et redevient à faire. Renvoie vrai si la tâche a été reposée.
     */
    suspend fun onCompleted(db: TeddDatabase, taskId: String, today: String = TODAY_ISO): Boolean =
        withContext(Dispatchers.Default) {
            val task = db.taskQueries.selectById(taskId).executeAsOneOrNull() ?: return@withContext false
            val rec = TaskRecurrence.of(task.recurrence)
            if (rec == TaskRecurrence.None) return@withContext false
            val base = task.due_date?.takeIf { org.teddapp.project.ui.TeddDates.isValidIso(it) } ?: today
            val next = nextOccurrence(rec, if (base < today) today else base) ?: return@withContext false
            val now = nowMillis()
            db.taskQueries.updateDue(next, now, taskId)
            db.taskQueries.markPending(now, taskId)
            true
        }
}
